<template>
  <!--
    Photographs and clips, from the three places they actually come from.

    It lives in the ui-kit for the same reason the recorder does: what it
    produces is a platform thing — Files that `platform.files` stores — and the
    ways it goes wrong are the same in every module. A parte, an incidencia, a
    task, a refuelling: four screens, one camera.

    ── Three buttons and not one ──────────────────────────────────────────────

    `<input type="file" capture="environment">` opens the camera straight away
    on a phone, and a photo taken now is a different intention from a photo
    found in the roll — different enough that hiding both behind one button
    costs a tap and a moment of "which one was it". On a desktop `capture` is
    ignored and all three open the same file dialog, which is fine: nobody there
    is choosing between a camera and a gallery.

    No `getUserMedia` anywhere in here, so this is not a second door past §4.5:
    a file input is the operating system's own picker, and the platform never
    sees the device. `useMediaCapture` is for a live stream — the recorder, the
    barcode scanner — and this is not one.

    ── What it hands back ─────────────────────────────────────────────────────

    An array of Files, already compressed if they were photographs. The parent
    uploads them whenever it uploads the rest of its form: nothing here talks to
    the network, so a picker on a form that is never saved has cost nothing.
  -->
  <div class="pick">
    <div v-if="items.length" class="pick__grid">
      <div v-for="(item, index) in items" :key="item.key" class="pick__cell">
        <button
          type="button"
          class="pick__thumb"
          :aria-label="item.file.name"
          @click="emit('open', index)"
        >
          <img v-if="item.kind === 'photo'" :src="item.url" alt="" class="pick__image" />
          <template v-else>
            <video :src="item.url" class="pick__image" preload="metadata" muted playsinline />
            <span class="pick__play" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            </span>
          </template>
        </button>

        <button
          type="button"
          class="pick__drop"
          :title="uiLabel('mediaRemove')"
          :aria-label="uiLabel('mediaRemove')"
          :disabled="disabled"
          @click="removeAt(index)"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <span v-if="item.kind === 'video'" class="pick__size">{{ megabytes(item.file.size) }}</span>
      </div>
    </div>

    <div v-if="!full" class="pick__buttons">
      <button type="button" class="pick__button" :disabled="disabled" @click="open('photo')">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
        {{ uiLabel('mediaPhoto') }}
      </button>

      <button v-if="video" type="button" class="pick__button" :disabled="disabled" @click="open('video')">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" />
        </svg>
        {{ uiLabel('mediaVideo') }}
      </button>

      <button type="button" class="pick__button" :disabled="disabled" @click="open('gallery')">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
        </svg>
        {{ uiLabel('mediaGallery') }}
      </button>
    </div>

    <p v-if="hint" class="pick__hint">{{ hint }}</p>
    <p v-if="problem" class="pick__problem" role="alert">{{ problem }}</p>

    <!--
      One input per origin rather than one whose attributes are rewritten before
      each click: changing `capture` on a live input is honoured by some
      browsers only after the next paint, so the first tap opened the gallery
      and the second opened the camera.
    -->
    <input
      ref="photoInput" type="file" class="pick__input"
      :accept="ACCEPT.photo" capture="environment" multiple
      @change="onPicked"
    />
    <input
      ref="videoInput" type="file" class="pick__input"
      :accept="ACCEPT.video" capture="environment"
      @change="onPicked"
    />
    <input
      ref="galleryInput" type="file" class="pick__input"
      :accept="video ? ACCEPT.media : ACCEPT.photo" multiple
      @change="onPicked"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import { compressImage } from '../utils/image'
import { ACCEPT, mediaKind } from '../utils/media'
import { uiLabel } from '../utils/labels'

const props = defineProps({
  /** The files chosen so far. An array of File. */
  modelValue: { type: Array, default: () => [] },
  /** Offer the video origin at all. A screen that only wants photographs says no. */
  video: { type: Boolean, default: true },
  /** How many in total. Zero means no ceiling. */
  max: { type: Number, default: 0 },
  /**
   * The clip ceiling in bytes, said here so somebody learns it BEFORE waiting
   * out an upload. It has to match `FILES_MAX_VIDEO_BYTES` on the instance —
   * the backend is what enforces it; this only saves the round trip.
   */
  maxVideoBytes: { type: Number, default: 90 * 1024 * 1024 },
  /** One line under the buttons, from the screen: what these photographs are for. */
  hint: { type: String, default: '' },
  disabled: Boolean,
})

const emit = defineEmits(['update:modelValue', 'open'])

const photoInput = ref(null)
const videoInput = ref(null)
const galleryInput = ref(null)
const problem = ref('')

const full = computed(() => props.max > 0 && props.modelValue.length >= props.max)

// ── The object URLs, and why they are kept in a list ─────────────────────────
//
// createObjectURL hands out a reference the browser holds until it is revoked.
// A computed would mint a new one on every re-render and leak a whole
// photograph each time — which nobody sees until a long shift has filled the
// tab with twelve-megapixel frames. So one URL per file, minted when the list
// changes and revoked when it changes again.
const items = ref([])

watch(() => props.modelValue, (files) => {
  for (const item of items.value) URL.revokeObjectURL(item.url)
  items.value = (files ?? []).map((file, index) => ({
    key: `${index}-${file.name}-${file.size}`,
    file,
    kind: mediaKind(file.type),
    url: URL.createObjectURL(file),
  }))
}, { immediate: true, deep: false })

onBeforeUnmount(() => {
  for (const item of items.value) URL.revokeObjectURL(item.url)
})

const megabytes = (bytes) => `${(bytes / 1024 / 1024).toFixed(1)} MB`

function open(origin) {
  if (props.disabled || full.value) return
  problem.value = ''
  const input = { photo: photoInput, video: videoInput, gallery: galleryInput }[origin]
  input.value?.click()
}

async function onPicked(event) {
  const input = event.target
  const chosen = [...(input.files ?? [])]
  // Cleared straight away, and before the awaits below: picking the same
  // photograph twice in a row fires no change event otherwise, and the second
  // attempt looks like the button is broken.
  input.value = ''
  if (!chosen.length) return

  const room = props.max > 0 ? props.max - props.modelValue.length : chosen.length
  const taken = chosen.slice(0, Math.max(0, room))
  if (taken.length < chosen.length) problem.value = uiLabel('mediaFull')

  const kept = []
  for (const file of taken) {
    if (mediaKind(file.type) === 'video' && file.size > props.maxVideoBytes) {
      // Said here rather than after the upload, because the upload is the
      // expensive part: ninety megabytes over 4G is a minute of waiting to be
      // told the thing was too big before it started.
      problem.value = uiLabel('mediaTooBig').replace(
        '{max}', `${Math.round(props.maxVideoBytes / 1024 / 1024)} MB`
      )
      continue
    }
    // Photographs shrink; clips, PDFs and anything else go through untouched.
    kept.push(await compressImage(file))
  }

  if (kept.length) emit('update:modelValue', [...props.modelValue, ...kept])
}

function removeAt(index) {
  const next = [...props.modelValue]
  next.splice(index, 1)
  problem.value = ''
  emit('update:modelValue', next)
}
</script>

<style scoped>
.pick { display: flex; flex-direction: column; gap: var(--space-3); }

.pick__input { display: none; }

.pick__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(84px, 100%), 1fr));
  gap: var(--space-2);
}

.pick__cell { position: relative; }

.pick__thumb {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  padding: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface-2);
  overflow: hidden;
  cursor: pointer;
}
.pick__thumb:hover { border-color: var(--color-primary); }

.pick__image { width: 100%; height: 100%; object-fit: cover; display: block; }

/* A clip has to read as a clip in a grid of photographs, and a poster frame on
   its own does not — half the time the first frame is a dark ceiling. */
.pick__play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.pick__play svg {
  width: 30px;
  height: 30px;
  padding: 8px;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.6);
  color: #fff;
}

.pick__size {
  position: absolute;
  left: var(--space-1);
  bottom: var(--space-1);
  padding: 1px 5px;
  border-radius: var(--radius-sm);
  background: rgba(15, 23, 42, 0.65);
  color: #fff;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
}

.pick__drop {
  position: absolute;
  top: -6px;
  right: -6px;
  /* 26px of ink, 44px of target: it sits on the corner of a thumbnail that a
     thumb is also trying to tap, and the two must not fight. */
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border);
  border-radius: 50%;
  background: var(--color-surface);
  color: var(--color-text-soft);
  cursor: pointer;
}
.pick__drop:hover:not(:disabled) { border-color: var(--color-danger); color: var(--color-danger); }
.pick__drop:disabled { opacity: 0.5; cursor: not-allowed; }

.pick__buttons { display: flex; flex-wrap: wrap; gap: var(--space-2); }

.pick__button {
  flex: 1 1 auto;
  min-width: 104px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  /* A field control on a phone, so tall enough to hit with a glove on. */
  padding: var(--space-3) var(--space-4);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
  color: var(--color-text-soft);
  font-size: var(--text-sm);
  font-weight: 500;
  cursor: pointer;
  transition: border-color var(--fast), color var(--fast);
}
.pick__button:hover:not(:disabled) { border-color: var(--color-primary); color: var(--color-primary); }
.pick__button:disabled { opacity: 0.55; cursor: not-allowed; }

.pick__hint { margin: 0; font-size: var(--text-xs); color: var(--color-text-mute); }
.pick__problem { margin: 0; font-size: var(--text-xs); color: var(--color-danger); }
</style>
